import { motion, type Transition } from 'framer-motion';
import { springs } from '../../../ui/slide-animations';
import { usePrefersReducedMotion } from '../../../../hooks/usePrefersReducedMotion';

type RevealVariant = 'fade' | 'up' | 'scale';

const HIDDEN: Record<RevealVariant, Record<string, number>> = {
  fade: { opacity: 0 },
  up: { opacity: 0, y: 20 },
  scale: { opacity: 0, scale: 0.96 },
};

const SHOWN: Record<RevealVariant, Record<string, number>> = {
  fade: { opacity: 1 },
  up: { opacity: 1, y: 0 },
  scale: { opacity: 1, scale: 1 },
};

export interface RevealProps {
  children: React.ReactNode;
  /** Entrance style. `up` (fade + rise) is the default reveal beat. */
  variant?: RevealVariant;
  /** Seconds to wait before animating in. */
  delay?: number;
  /** Spring preset from slide-animations. Defaults to `gentle`. */
  spring?: keyof typeof springs;
  className?: string;
  style?: React.CSSProperties;
  /** Forwarded so AnimatePresence can key/track this reveal. */
  layout?: boolean;
}

/**
 * Single-element entrance reveal for a slide's payoff moment (an answer
 * appearing, a layer unlocking, a comparison resolving). Mounts from hidden →
 * shown using a shared spring, and collapses to instant when the user prefers
 * reduced motion. For exit transitions, wrap a keyed <Reveal> in <AnimatePresence>.
 */
export function Reveal({
  children,
  variant = 'up',
  delay = 0,
  spring = 'gentle',
  className,
  style,
  layout,
}: RevealProps) {
  const reduced = usePrefersReducedMotion();

  const transition: Transition = reduced
    ? { duration: 0 }
    : { ...(springs[spring] as Transition), delay };

  return (
    <motion.div
      className={className}
      style={style}
      layout={layout}
      initial={reduced ? false : HIDDEN[variant]}
      animate={SHOWN[variant]}
      exit={reduced ? { opacity: 0 } : HIDDEN[variant]}
      transition={transition}
    >
      {children}
    </motion.div>
  );
}
